import { RenderContext } from '../context/createRenderContext';
import { TextNode } from '../text';
import { getCacheByType } from './raster';
import { charWidth, diffText, measureText } from './text';

// 判断字符是否为CJK字符（中日韩统一表意文字）
const isCJK = (char) => {
  const code = char.charCodeAt(0);
  return (
    (code >= 0x4e00 && code <= 0x9fff) ||
    (code >= 0x3400 && code <= 0x4dbf) ||
    (code >= 0x20000 && code <= 0x2a6df) ||
    (code >= 0x2a700 && code <= 0x2b73f) ||
    (code >= 0x2b740 && code <= 0x2b81f) ||
    (code >= 0x2b820 && code <= 0x2ceaf) ||
    (code >= 0xf900 && code <= 0xfaff) ||
    (code >= 0x2f800 && code <= 0x2fa1f)
  );
};

// 将文本按“书写字素(grapheme)”分割，保证 ZWJ 序列、国旗、键帽等作为一个整体
export const splitGraphemes = (text: string) => {
  // Prefer Intl.Segmenter when available (modern browsers/Node)
  if (typeof Intl !== 'undefined' && Intl.Segmenter) {
    const seg = new Intl.Segmenter(undefined, { granularity: 'grapheme' });
    return Array.from(seg.segment(text), (s) => s.segment);
  }
  // Fallback: Array.from iterates code points; we add minimal joins for common emoji clusters
  const cps = Array.from(text);
  const out: string[] = [];
  for (let i = 0; i < cps.length; i++) {
    let g = cps[i];

    // Keycap sequences: [0-9#*] + VS16? + U+20E3
    if (/[0-9#*]/.test(g)) {
      if (cps[i + 1] === '\uFE0F' && cps[i + 2] === '\u20E3') {
        g += cps[i + 1] + cps[i + 2];
        i += 2;
        out.push(g);
        continue;
      }
      if (cps[i + 1] === '\u20E3') {
        g += cps[i + 1];
        i += 1;
        out.push(g);
        continue;
      }
    }

    // Regional indicator flags: pair of RI code points
    const isRI = (ch) => /^[\u{1F1E6}-\u{1F1FF}]$/u.test(ch || '');
    if (isRI(g) && isRI(cps[i + 1])) {
      g += cps[i + 1];
      i += 1;
      out.push(g);
      continue;
    }

    // Emoji + modifiers/VS16/ZWJ sequences (heuristic)
    // Broad pictographic range for fallback
    const EP_RE = /[\u{1F300}-\u{1FAFF}\u{2600}-\u{27BF}]/u;
    while (i + 1 < cps.length) {
      const next = cps[i + 1];
      if (next === '\uFE0F') {
        // VS16
        g += next;
        i += 1;
        continue;
      }
      if (/^[\u{1F3FB}-\u{1F3FF}]$/u.test(next)) {
        // skin tone modifiers
        g += next;
        i += 1;
        continue;
      }
      if (next === '\u200D' && i + 2 < cps.length) {
        // ZWJ + pictograph
        const afterZWJ = cps[i + 2];
        if (EP_RE.test(afterZWJ)) {
          g += next + afterZWJ;
          i += 2;
          continue; // allow chaining multiple ZWJ joins
        }
      }
      break;
    }
    out.push(g);
  }
  return out;
};

// 判断字素是否为 Emoji（基于 Extended_Pictographic/区域指示符/键帽序列）
let EXTENDED_PICTO_RE;
try {
  EXTENDED_PICTO_RE = /\p{Extended_Pictographic}/u;
} catch {
  // Fallback: 常见 emoji 块的粗略覆盖
  EXTENDED_PICTO_RE = /[\u{1F300}-\u{1FAFF}\u{2600}-\u{27BF}]/u;
}
const REGIONAL_FLAG_RE = /^[\u{1F1E6}-\u{1F1FF}]{2}$/u; // 🇦-🇿 两个组成的国旗
const KEYCAP_RE = /^(?:[#*0-9]\uFE0F?\u20E3)$/u; // 键帽

const isEmoji = (grapheme) => {
  if (!grapheme) return false;
  return (
    KEYCAP_RE.test(grapheme) || REGIONAL_FLAG_RE.test(grapheme) || EXTENDED_PICTO_RE.test(grapheme)
  );
};

// Unified function to check if a character belongs to various non-Latin writing systems
const isNonLatinChar = (token) => {
  return (
    isCJK(token) ||
    isEmoji(token) ||
    /[\u0600-\u06FF]/.test(token) || // Arabic
    /[\u0900-\u097F]/.test(token) || // Devanagari (Hindi, Sanskrit)
    /[\u0E00-\u0E7F]/.test(token) || // Thai
    /[\u0590-\u05FF]/.test(token) || // Hebrew
    /[\u1100-\u11FF\uAC00-\uD7AF]/.test(token)
  ); // Korean
};

// 判断字符是否为标点符号
const isPunctuationMark = (char) => {
  // ASCII 标点符号
  const asciiPunctuation = /[!"#$%&'()*+,\-./:;<=>?@[\\\]^_`{|}~]/;
  // CJK 标点符号
  const cjkPunctuation =
    /[。，、；：！？"'（）【】《》「」『』〈〉〔〕〖〗＜＞…—～＋－×÷＝≠＜＞≤≥％‰‱°℃℉Ω¥￥＄￠€£¢₩₪§⁰¹²³⁴⁵⁶⁷⁸⁹₀₁₂₃₄₅₆₇₈₉]/;
  // 其他常见标点符号
  const otherPunctuation = /[·•‚„‰′″‴‹›※‼⁇⁈⁉⁏⁒]/;

  return asciiPunctuation.test(char) || cjkPunctuation.test(char) || otherPunctuation.test(char);
};

// 判断字符是否为空白字符
const isWhitespace = (char) => {
  return /\s/.test(char);
};

// 判断字符是否为行首不可出现的字符
const isNoLineStartChar = (char) => {
  // 行首不可出现的标点符号
  return /[!%),.:;?]}¢°'"‰′″‴〉》」』】〕〗〙〛゛゜ゝゞゟヽヾーァィゥェォッャュョヮヵヶぁぃぅぇぉっゃゅょゎゕゖㇰㇱㇲㇳㇴㇵㇶㇷㇸㇹㇺㇻㇼㇽㇾㇿ々〻‐–—''""…‥・、。〜〽〾〿]/.test(
    char
  );
};

// 判断字符是否为结尾标点符号（应该与前面内容保持在同一行）
const isEndingPunctuation = (char) => {
  // 常见的句末标点符号，应该避免单独换行
  return /[。！？；，、：]/.test(char);
};

// 判断字符是否为行尾不可出现的字符
const isNoLineEndChar = (char) => {
  // 行尾不可出现的字符
  return /[($[¥'"〈《「『【〔〖〘〚〝（［｛｢￡￠\u00a3\u00a5\u20a9\u20aa]/.test(char);
};

type CharToken = {
  type: 'whitespace' | 'punctuation' | 'cjk' | 'word';
  content: string;
};

export const parseTokens = (text) => {
  const tokens: CharToken[] = [];
  // 使用 grapheme 分割，确保 emoji ZWJ 序列等不会被拆开
  const graphemeList = splitGraphemes(text);
  let i = 0;

  while (i < graphemeList.length) {
    const char = graphemeList[i];

    if (isWhitespace(char)) {
      // Handle consecutive whitespace characters
      let whitespace = char;
      i++;
      while (i < graphemeList.length && isWhitespace(graphemeList[i])) {
        whitespace += graphemeList[i];
        i++;
      }
      tokens.push({ type: 'whitespace', content: whitespace });
    } else if (isPunctuationMark(char)) {
      // Punctuation marks are individual tokens
      tokens.push({ type: 'punctuation', content: char });
      i++;
    } else if (isCJK(char) || isEmoji(char)) {
      // CJK 与 Emoji（包含 ZWJ 序列、国旗、键帽等）作为独立 token
      tokens.push({ type: 'cjk', content: char });
      i++;
    } else {
      // Handle normal characters (like English words)
      let word = char;
      i++;
      while (
        i < graphemeList.length &&
        !isWhitespace(graphemeList[i]) &&
        !isPunctuationMark(graphemeList[i]) &&
        !isCJK(graphemeList[i]) &&
        !isEmoji(graphemeList[i])
      ) {
        word += graphemeList[i];
        i++;
      }
      tokens.push({ type: 'word', content: word });
    }
  }

  return tokens;
};

// Heuristic helpers to keep Latin words with adjacent joiner punctuation together (e.g. "Lv.0", "v2.1", "foo-bar")
const isAsciiWord = (str) => /^[A-Za-z0-9]+$/.test(str);
const isAsciiNumber = (str) => /^[0-9]+$/.test(str);
const isAsciiJoinerPunctuation = (str) => /^[._:/\-+@#%']$/.test(str);

// Merge sequences like WORD (JOINER WORD)+ into a single token to avoid breaking inside Latin clusters
function coalesceLatinClusters(tokens: CharToken[]) {
  const merged: CharToken[] = [];
  let i = 0;
  while (i < tokens.length) {
    const t = tokens[i];
    if (t.type === 'word' && isAsciiWord(t.content)) {
      let cluster = t.content;
      let j = i + 1;
      // Continue while we see (joiner punctuation + ascii word/number) pairs
      while (
        j + 1 < tokens.length &&
        tokens[j].type === 'punctuation' &&
        isAsciiJoinerPunctuation(tokens[j].content) &&
        tokens[j + 1].type === 'word' &&
        isAsciiWord(tokens[j + 1].content)
      ) {
        cluster += tokens[j].content + tokens[j + 1].content;
        j += 2;
      }
      // Special case: check for trailing joiner punctuation followed by numbers only
      // This handles cases like "Lv." where the period might be at the end
      while (
        j < tokens.length &&
        tokens[j].type === 'punctuation' &&
        isAsciiJoinerPunctuation(tokens[j].content)
      ) {
        // Look ahead to see if there's a number after this punctuation
        if (
          j + 1 < tokens.length &&
          tokens[j + 1].type === 'word' &&
          isAsciiNumber(tokens[j + 1].content)
        ) {
          cluster += tokens[j].content + tokens[j + 1].content;
          j += 2;
        } else {
          break;
        }
      }
      merged.push({ type: 'word', content: cluster });
      i = j;
      continue;
    }
    merged.push(t);
    i += 1;
  }
  return merged;
}

export const internalFontSettings = {
  Arial: {
    fontFamily: '"Arial"',
    lineHeight: 1.35,
    margin: 12,
  },
};

export const defaultFontSettings = internalFontSettings.Arial;

export const getLineWidth = (text, font, letterSpacing) => {
  return measureText(text, font, defaultFontSettings.lineHeight, letterSpacing).width;
};

const emptyTextWrap = {
  lines: [] as string[][],
  width: 0,
  linesBreakType: [],
};

export enum WrapType {
  HARD = 0,
  SOFT = 1,
}

export const wrapTextFn = (text: string, font, maxWidth, fontSettings) => {
  if (!text)
    return {
      ...emptyTextWrap,
      height: fontSettings.lineHeight as number,
    };

  text = text || '';
  let hardLine = true;
  const lines: string[] = [];
  // Track the type of break applied to each line: 0 = hard (newline), 1 = soft (wrapping)
  const linesBreakType: WrapType[] = [];
  const originalLines = text.split('\n');
  const pushLine = (line) => {
    lines.push(line);
    linesBreakType.push(hardLine ? WrapType.HARD : WrapType.SOFT);
    hardLine = false;
  };

  // this part should be optimized to handle the space and punctuation better.
  for (const originalLine of originalLines) {
    hardLine = true;
    // If the original line is empty, add an empty line
    if (originalLine === '') {
      pushLine('');
      continue;
    }

    // Check if the whole line fits
    const lineWidth = getLineWidth(originalLine, font, fontSettings.letterSpacing);
    if (lineWidth <= maxWidth) {
      pushLine(originalLine);
      continue;
    }

    // Needs wrapping
    let tokens = parseTokens(originalLine);
    // Merge Latin clusters like "Lv.0" to improve CJK/Latin mixed wrapping
    tokens = coalesceLatinClusters(tokens);
    let currentLine = '';
    let i = 0;

    while (i < tokens.length) {
      const token = tokens[i];
      const tokenWidth = getLineWidth(token.content, font, fontSettings.letterSpacing);

      // Special handling for whitespace: follow leading-space rules
      if (token.type === 'whitespace') {
        // If we're at the beginning of a new wrapped line
        if (currentLine === '') {
          const ws = token.content;
          if (ws === ' ') {
            // Single leading space → move to previous line's end (if any) to avoid starting with a space
            if (lines.length > 0) {
              lines[lines.length - 1] = (lines[lines.length - 1] || '') + ' ';
            }
            i++;
            continue;
          } else {
            // Two or more spaces (or other whitespace) → keep as leading characters
            currentLine = ws;
            i++;
            continue;
          }
        }

        // Look ahead to the next non-whitespace token
        let j = i + 1;
        while (j < tokens.length && tokens[j].type === 'whitespace') j++;
        const widthWithSpaceOnly = getLineWidth(
          currentLine + token.content,
          font,
          fontSettings.letterSpacing
        );

        if (j < tokens.length) {
          const nextToken = tokens[j];
          const widthWithSpaceAndNext = getLineWidth(
            currentLine + token.content + nextToken.content,
            font,
            fontSettings.letterSpacing
          );

          if (widthWithSpaceAndNext > maxWidth) {
            // Break before next token. Apply special rules for leading spaces on the new line.
            const ws = token.content;
            if (ws === ' ') {
              // Single space → move to previous line even if it slightly exceeds width
              currentLine = currentLine + ' ';
              pushLine(currentLine);
              currentLine = '';
            } else {
              // Two or more spaces → start the next line with these spaces to preserve alignment
              pushLine(currentLine);
              currentLine = ws;
            }
            i++;
            continue;
          }
        }

        // Otherwise, add whitespace if it alone doesn't overflow; if it does, break without carrying it
        if (widthWithSpaceOnly > maxWidth) {
          if (currentLine !== '') {
            pushLine(currentLine);
          }
          currentLine = '';
          i++;
          continue;
        }

        currentLine += token.content;
        i++;
        continue;
      }

      // Handle oversized single tokens
      if (tokenWidth > maxWidth) {
        // If the current line is not empty, finish it first
        if (currentLine !== '') {
          pushLine(currentLine);
          currentLine = '';
        }

        // For oversized words, split them at the character level
        if (token.type === 'word') {
          let charLine = '';
          for (const char of token.content) {
            const potentialWidth = getLineWidth(charLine + char, font, fontSettings.letterSpacing);

            if (potentialWidth > maxWidth && charLine !== '') {
              pushLine(charLine);
              charLine = '';
            }
            charLine += char;
          }

          if (charLine !== '') {
            currentLine = charLine;
          }
        } else {
          // For other types of oversized tokens, put them on their own line
          pushLine(token.content);
        }

        i++;
        continue;
      }

      const potentialLine = currentLine + token.content;
      const potentialWidth = getLineWidth(potentialLine, font, fontSettings.letterSpacing);

      // Check if adding the current token would exceed the width
      if (potentialWidth > maxWidth && currentLine !== '') {
        // Handle characters that cannot start a line (like closing punctuation)
        if (token.type === 'punctuation' && isNoLineStartChar(token.content)) {
          // If it's a character that can't start a line, add it to the previous line
          // even if it exceeds the width slightly - this is better than having it on a new line
          currentLine += token.content;
          i++;
          continue;
        }

        // For ending punctuation marks (like Chinese periods, commas, etc.),
        // prefer keeping them with the current line to avoid orphaned punctuation
        if (
          token.type === 'punctuation' &&
          (isEndingPunctuation(token.content) || tokenWidth <= maxWidth * 0.1)
        ) {
          // If it's an ending punctuation or relatively small (≤10% of line width),
          // keep it with the current line to avoid orphaned punctuation
          currentLine += token.content;
          i++;
          continue;
        }

        // Check for characters that cannot end a line
        if (currentLine.length > 0) {
          const lastChar = currentLine[currentLine.length - 1];
          if (isNoLineEndChar(lastChar)) {
            // If the line ends with a forbidden character, try to adjust
            let j = currentLine.length - 1;
            let adjustedLine = currentLine;

            // Find a valid break point
            while (j >= 0 && isNoLineEndChar(adjustedLine[j])) {
              j--;
            }

            if (j >= 0) {
              // Move the characters that can't end a line to the next line
              const moveToNext = adjustedLine.substring(j + 1);
              adjustedLine = adjustedLine.substring(0, j + 1);

              pushLine(adjustedLine);
              currentLine = moveToNext + token.content;
            } else {
              // If the whole line consists of non-breaking characters, force a break
              pushLine(currentLine);
              currentLine = token.content;
            }
          } else {
            // Normal line break
            pushLine(currentLine);
            currentLine = token.content;
          }
        } else {
          // Avoid emitting empty lines when the current line is empty
          currentLine = token.content;
        }
      } else {
        // Can be added to the current line
        currentLine = potentialLine;
      }

      i++;
    }

    // Add the last line
    if (currentLine !== '') {
      pushLine(currentLine);
    }
  }

  const textMetrics = measureText(
    lines.join('\n'),
    font,
    fontSettings.lineHeight,
    fontSettings.letterSpacing
  );
  const linesOfGraphemes = lines.map(splitGraphemes);

  return {
    width: textMetrics.width,
    height: textMetrics.height,
    lines: linesOfGraphemes,

    /**
     * The type of break applied to each line:
     * 0 = hard break (explicit newline in original text)
     * 1 = soft break (automatic wrapping)
     */
    linesBreakType,
  };
};

// === Shared mapping utilities (to keep Provider and Renderer consistent) ===
export function mapWrappedIndexToOriginal(original, wrapped, wrappedIndex) {
  const diffs = diffText(original || '', wrapped || '');
  const map: Array<number> = [];
  let o = 0;
  let w = 0;
  for (const d of diffs) {
    if (d.added) {
      // only in wrapped
      map[w] = o;
      w += 1;
    } else if (d.removed) {
      // only in original
      o += 1;
    } else {
      // common
      map[w] = o;
      o += 1;
      w += 1;
    }
  }
  map[w] = o;
  const idx = Math.max(0, Math.min(wrappedIndex || 0, map.length - 1));
  return map[idx] ?? o;
}

export function mapOriginalIndexToWrapped(original, wrapped, originalIndex) {
  const diffs = diffText(original || '', wrapped || '');
  let o = 0;
  let w = 0;
  const map: Array<number> = [];
  for (const d of diffs) {
    if (d.added) {
      // only in wrapped
      map[o] = w;
      w += 1;
    } else if (d.removed) {
      // only in original
      o += 1;
    } else {
      // common
      map[o] = w;
      o += 1;
      w += 1;
    }
  }
  map[o] = w;
  const idx = Math.max(0, Math.min(originalIndex || 0, map.length - 1));
  return map[idx] ?? w;
}

export function isTextSupported(node: TextNode, text: string, renderContext: RenderContext) {
  if (!text) return true;

  const fontFamily = node.fontFamily || '';
  // Ensure we have real maps for caching, not noop caches
  const fontCheckCache = getCacheByType(renderContext || {}, 'fontCheck');
  const fontLoadCache = getCacheByType(renderContext || {}, 'font');

  const shortKey = `${fontFamily}:${hashText(text)}`;
  if (fontCheckCache.get(shortKey)) return true;

  // Throttle negative checks per (font, text-hash)
  const now =
    renderContext && renderContext.api && typeof renderContext.api.now === 'function'
      ? renderContext.api.now()
      : performance && performance.now
        ? performance.now()
        : Date.now();
  const lastFailAt = negativeFontCheckCache.get(shortKey);
  if (lastFailAt && now - lastFailAt < FONT_RECHECK_INTERVAL_MS) {
    return false;
  }

  // Fast path: check font load state using FontFaceSet.check (cheap and cached by UA)
  const isFontLoaded = checkFont(fontFamily, text, fontLoadCache);
  if (!isFontLoaded) {
    // Kick off a background load to accelerate next frames
    requestFontLoad(renderContext, node, text);
    negativeFontCheckCache.set(shortKey, now);
    return false;
  }

  // If text is ASCII-only/Latin without special scripts, trust the font load state
  if (!hasNonLatinChar(text)) {
    fontCheckCache.set(shortKey, true);
    charWidth.resetCache(fontFamily);
    return true;
  }

  // Coverage check on a minimal unique sample to avoid heavy work
  const result = _isTextSupported(fontFamily, text);
  if (result) {
    fontCheckCache.set(shortKey, true);
    charWidth.resetCache(fontFamily);
    return true;
  }

  negativeFontCheckCache.set(shortKey, now);
  return false;
}

export function checkFont(fontFamily: string, text: string, caches) {
  if (!document || !document.fonts || !document.fonts.check) return false;
  fontFamily = fontFamily || '';

  // For Chinese fonts, optimize by using a hash of the full text as a cache key,
  // because Chinese fonts are loaded in chunks.
  // We can't just sample; we need to check the actual characters used.
  const cacheKey = `${fontFamily}:${text.length}:${hashText(text)}`;

  if (caches.get(cacheKey)) return true;

  try {
    // For Chinese fonts, the full text needs to be checked because the font is loaded on demand in chunks.
    // However, for performance, we check in batches.
    const isLoaded = checkTextInBatches(fontFamily, text);
    if (!isLoaded) {
      return false;
    }

    caches.set(cacheKey, true);
    charWidth.resetCache(fontFamily);
    return true;
  } catch (e) {
    // Handle potential errors in font checking
    console.warn(`Font check failed for ${fontFamily}`, e);
    return false;
  }
}

// Simple hash function to generate a hash for the text
function hashText(text) {
  let hash = 0;
  for (let i = 0; i < Math.min(text.length, 50); i++) {
    const char = text.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash; // Convert to 32-bit integer
  }
  return hash.toString(36);
}

// Character-level cache to store support status for each character in a font
const characterCache = new Map<string, Map<string, boolean>>();

// Check text in batches to reduce the overhead of a single check
function checkTextInBatches(fontFamily: string, text: string) {
  const batchSize = 20; // Check 20 characters per batch
  const uniqueChars = [...new Set(text.replace(/\s/g, ''))]; // Remove duplicates and whitespace

  // Get the character cache for the font
  const fontCacheKey = fontFamily || 'default';
  if (!characterCache.has(fontCacheKey)) {
    characterCache.set(fontCacheKey, new Map());
  }
  const fontCharCache = characterCache.get(fontCacheKey)!;

  // Separate cached and uncached characters
  const uncachedChars: string[] = [];
  const cachedResults: boolean[] = [];

  for (const char of uniqueChars) {
    if (fontCharCache.has(char)) {
      const isSupported = fontCharCache.get(char)!;
      cachedResults.push(isSupported);
    } else {
      uncachedChars.push(char);
    }
  }

  // If all characters are cached and supported, return true
  if (uncachedChars.length === 0) {
    return true;
  }

  // For short text, check all uncached characters at once
  if (uncachedChars.length <= batchSize) {
    const isSupported = document.fonts.check(`1em "${fontFamily}"`, uncachedChars.join(''));

    // Cache the result at the character level
    if (isSupported) {
      for (const char of uncachedChars) {
        fontCharCache.set(char, isSupported);
      }
    }

    return isSupported;
  }

  // Check uncached characters in batches
  for (let i = 0; i < uncachedChars.length; i += batchSize) {
    const batch = uncachedChars.slice(i, i + batchSize);
    const batchText = batch.join('');
    const isSupported = document.fonts.check(`1em "${fontFamily}"`, batchText);

    // Cache the batch result at the character level
    if (isSupported) {
      for (const char of batch) {
        fontCharCache.set(char, isSupported);
      }
    }

    if (!isSupported) {
      return false;
    }
  }

  return true;
}

function _isTextSupported(fontFamily: string, text: string) {
  if (!text) return true;
  if (fontFamily === '-apple-system') return true;

  // Prefer sampling a small set of unique, non-Latin characters for coverage checks
  const sampleText =
    text.length > 160 ? text.substring(0, 80) + text.substring(text.length - 80) : text;

  const seen = new Set();
  const sampleChars: string[] = [];

  // First collect non-Latin unique chars
  for (const ch of [...sampleText]) {
    if (ch.match(/\s/) || ch.charCodeAt(0) < 32) continue;
    if (!isNonLatinChar(ch)) continue;
    if (seen.has(ch)) continue;
    seen.add(ch);
    sampleChars.push(ch);
    if (sampleChars.length >= 16) break;
  }

  // If there are no non-Latin chars, assume supported once the font is reported loaded
  if (sampleChars.length === 0) return true;

  // Optionally add a few ASCII letters to guard against accidental fallbacks
  for (const ch of [...sampleText]) {
    if (sampleChars.length >= 20) break;
    if (ch.match(/\s/) || ch.charCodeAt(0) < 32) continue;
    if (isNonLatinChar(ch)) continue;
    if (/[^\p{L}\p{N}]/u.test(ch)) continue; // skip punctuation/symbol already considered supported
    if (seen.has(ch)) continue;
    seen.add(ch);
    sampleChars.push(ch);
  }

  for (const ch of sampleChars) {
    if (!isCharacterSupported(fontFamily, ch)) {
      return false;
    }
  }

  return true;
}

const SIZE = 24;
let canvas, ctx;

// Safely initialize the Canvas context
// Note: OffscreenCanvas may not have access to the main document's fonts, so prefer a regular Canvas
function initializeCanvas() {
  if (canvas) return true;
  try {
    if (typeof document !== 'undefined') {
      // Browser environment - prefer regular Canvas to ensure access to document fonts
      canvas = document.createElement('canvas');
      canvas.width = SIZE;
      canvas.height = SIZE;

      // document.body.append(canvas)
      // canvas.style.position = 'fixed';
      // canvas.style.top = '100px'
      // canvas.style.left = '100px'
      // canvas.style.zIndex = '1000000';
      // canvas.style.border = '1px solid red';

      ctx = canvas.getContext('2d', {
        willReadFrequently: true,
      });
    } else if (typeof OffscreenCanvas !== 'undefined') {
      // OffscreenCanvas as a fallback
      // Warning: OffscreenCanvas may not have access to dynamically loaded fonts in the main document
      canvas = new OffscreenCanvas(SIZE, SIZE);
      ctx = canvas.getContext('2d', {
        willReadFrequently: true,
      });
    } else {
      // Node.js or other environments where Canvas is not available
      return false;
    }
    return ctx !== null;
  } catch (error) {
    console.warn('Failed to initialize canvas for font detection:', error);
    return false;
  }
}

// Cache & reference characters
const cache = new Map();

// Internal helper: render a single character and return pixel data
function _render(char, font) {
  initializeCanvas();
  if (!canvas) {
    throw new Error('Canvas not available for font detection');
  }

  try {
    ctx.clearRect(0, 0, SIZE, SIZE);
    ctx.font = `22px "${font}"`;
    ctx.textBaseline = 'middle';
    ctx.textAlign = 'center';
    ctx.fillStyle = '#000';
    ctx.fillText(char, SIZE / 2, SIZE / 2);
    return ctx.getImageData(0, 0, SIZE, SIZE);
  } catch (error) {
    console.warn('Failed to render character for font detection:', error);
    return null;
  }
}

// Internal helper: calculate the pixel difference between two ImageData objects
function _diff(a, b) {
  if (!a || !b || !a.data || !b.data) return Infinity;

  // Check if data lengths are consistent
  if (a.data.length !== b.data.length) return Infinity;

  let diff = 0;
  let pixelCount = 0;

  // Iterate over all RGBA pixel data
  for (let i = 0; i < a.data.length; i += 4) {
    // Calculate the difference for the four RGBA channels
    const rDiff = a.data[i] - b.data[i]; // R channel
    const gDiff = a.data[i + 1] - b.data[i + 1]; // G channel
    const bDiff = a.data[i + 2] - b.data[i + 2]; // B channel
    const aDiff = a.data[i + 3] - b.data[i + 3]; // A channel

    // Accumulate the sum of squared differences
    diff += rDiff * rDiff + gDiff * gDiff + bDiff * bDiff + aDiff * aDiff;
    pixelCount++;
  }

  // Return the average difference, avoiding returning 0
  return pixelCount > 0 ? (diff / pixelCount) * 10000 : 0;
}

/**
 * Check if a character is supported by the specified font
 * @param {string} fontFamily  Font name
 * @param {string} character   A single character
 * @param {number} threshold   Pixel difference threshold (empirical value 0.02)
 * @returns {boolean}
 */
function isCharacterSupported(fontFamily = '', character, threshold = 0.02) {
  if (typeof character !== 'string' || character.length === 0) {
    throw new Error('must input valid character');
  }

  // Handle emoji and multi-byte characters (surrogate pairs)
  const chars = [...character];
  if (chars.length !== 1) {
    throw new Error('must input single character');
  }

  // Skip detection for punctuation marks, assume they are supported
  if (/[\p{P}\p{S}]/u.test(character)) {
    return true;
  }

  // If Canvas is not available, default to assuming the character is supported
  if (!initializeCanvas()) {
    return true;
  }

  const key = `${fontFamily}\0${character}`;
  const cachedResult = cache.get(key);

  // Only return the cached result if it's true (supported)
  // If previously detected as not supported, re-check because the font might have loaded asynchronously
  if (cachedResult === true) {
    return true;
  }

  try {
    // Render with the target font
    const target = _render(character, fontFamily);
    if (!target) {
      // If rendering fails, don't cache, allow for a retry next time
      return true;
    }

    // Check fallback font cache
    const fallbackKey = `sans-serif\0${character}`;
    let fallback = cache.get(fallbackKey);

    if (!fallback) {
      // Compare with a fallback font
      fallback = _render(character, 'sans-serif');
      if (!fallback) {
        // If fallback font rendering fails, don't cache, allow for a retry
        return true;
      }
      // Cache the rendering result of the fallback font
      cache.set(fallbackKey, fallback);
    }

    const delta = _diff(target, fallback) / (255 * 255 * SIZE * SIZE);
    const supported = delta > threshold;

    // Only cache supported results; unsupported results are not cached to allow for retries
    if (supported) {
      cache.set(key, true);
      cache.delete(fallbackKey);
    }

    return supported;
  } catch (error) {
    console.warn('Error in font character support detection:', error);
    // On error, don't cache, allow for a retry next time
    return true;
  }
}

// High-level helpers for performance
const FONT_RECHECK_INTERVAL_MS = 500;
const negativeFontCheckCache = new Map(); // key -> timestamp of last negative
const fontLoadPromises = new Map(); // key -> Promise

function hasNonLatinChar(text) {
  for (const ch of [...text]) {
    if (ch.match(/\s/) || ch.charCodeAt(0) < 32) continue;
    if (isNonLatinChar(ch)) return true;
  }
  return false;
}

function requestFontLoad(renderContext, node, text) {
  try {
    if (!document || !document.fonts || !document.fonts.load) return;
    const family = node.fontFamily;
    const key = `${family}:${hashText(text)}`;
    if (fontLoadPromises.has(key)) return;
    const p = document.fonts
      .load(`1em "${family}"`, text)
      .catch(console.error)
      .finally(() => {
        fontLoadPromises.delete(key);
        // On potential load completion, reset char width cache to re-measure correctly next frame
        charWidth.resetCache(family);
        renderContext?.api?.markNodeUpdate(node);
        renderContext?.api?.markNeedRender();
      });
    fontLoadPromises.set(key, p);
  } catch {}
}
